import {
  formatTokenAmountWithOptionalDecimals,
  safeValidateAddress,
  SimulationStatus,
  transactionSimulationResponseSchema,
  type EventLog,
  type SimulationAsset,
  type SimulationParty,
  type SimulationResolutionContext,
  type TransactionSimulationResponse,
  type WrappedDecodedParameter,
} from '@mls-wallet/shared-types';
import { maxUint256 } from 'viem';
import type {
  CompletedTransactionSimulationState,
  SimulationAllowance,
  SimulationBalanceChange,
  SimulationTokenActivity,
  SimulationTokenApproval,
} from './types';

/** Validated ERC-20 event values used to build the simulation presentation. */
type ParsedTokenEvent = {
  kind: 'transfer' | 'approval';
  asset: SimulationAsset;
  rawAmount: bigint;
  source: SimulationParty;
  destination: SimulationParty;
};

/** User-facing collections derived from one successful simulation response. */
type DerivedSimulationPresentation = {
  balanceChanges: SimulationBalanceChange[];
  tokenApprovals: SimulationTokenApproval[];
  tokenActivities: SimulationTokenActivity[];
};

/**
 * Decodes a backend event parameter's serialized `rawValue`. Malformed or
 * unsupported values return undefined so one bad log cannot break the full
 * simulation summary.
 */
function decodedRawValue(input: WrappedDecodedParameter): string | undefined {
  try {
    const decoded: unknown = JSON.parse(input.value.value);
    // Case: the decoded parameter is not the backend's raw-value envelope.
    if (
      typeof decoded !== 'object' ||
      decoded === null ||
      !('rawValue' in decoded)
    ) {
      return undefined;
    }

    const rawValue = decoded.rawValue;
    return typeof rawValue === 'string' ? rawValue : undefined;
  } catch {
    // Case: an individual provider parameter contains malformed JSON.
    return undefined;
  }
}

/**
 * Convert a decoded Transfer or Approval log into a stable internal event.
 * The event contract determines the token; its three values represent
 * sender/owner, recipient/spender, and the raw base-unit amount.
 */
function parseTokenEvent(
  event: EventLog,
  resolution: SimulationResolutionContext
): ParsedTokenEvent | undefined {
  // Case: ignore logs that are not a supported three-value token event.
  if (
    (event.name !== 'Transfer' && event.name !== 'Approval') ||
    event.inputs.length !== 3
  ) {
    return undefined;
  }

  const [sourceInput, destinationInput, amountInput] = event.inputs;
  // Case: a malformed tuple is missing a required owner/from, spender/to, or amount value.
  if (
    sourceInput === undefined ||
    destinationInput === undefined ||
    amountInput === undefined
  ) {
    return undefined;
  }

  const sourceAddress = safeValidateAddress(
    decodedRawValue(sourceInput) ?? ''
  ).checksumAddress;
  const destinationAddress = safeValidateAddress(
    decodedRawValue(destinationInput) ?? ''
  ).checksumAddress;
  const tokenAddress = safeValidateAddress(event.raw.address).checksumAddress;
  const rawAmount = decodedRawValue(amountInput);

  // Case: a decoded event field is missing or is not a valid EVM address.
  if (
    sourceAddress === undefined ||
    destinationAddress === undefined ||
    tokenAddress === undefined ||
    rawAmount === undefined
  ) {
    return undefined;
  }

  let amount: bigint;
  try {
    amount = BigInt(rawAmount);
  } catch {
    // Case: the provider returned a non-integer token amount.
    return undefined;
  }

  // Case: ERC-20 Transfer and Approval amounts are unsigned integers.
  if (amount < 0n) {
    return undefined;
  }

  // Transfer(address,address,uint256) is shared by ERC-20 and ERC-721. Only
  // metadata that has already resolved the emitter as ERC-20 authorizes amount
  // formatting and balance aggregation.
  const resolvedAsset = resolution.assetsByAddress[tokenAddress.toLowerCase()];
  // Case: the emitter is unresolved, is not ERC-20, or has unsafe decimal metadata.
  if (
    resolvedAsset?.kind !== 'erc20' ||
    !Number.isInteger(resolvedAsset.decimals) ||
    resolvedAsset.decimals < 0 ||
    resolvedAsset.decimals > 255
  ) {
    return undefined;
  }

  // Address-book labels are optional; retain the address when no label resolves.
  const source = resolution.partiesByAddress[sourceAddress.toLowerCase()] ?? {
    address: sourceAddress,
  };
  const destination = resolution.partiesByAddress[
    destinationAddress.toLowerCase()
  ] ?? {
    address: destinationAddress,
  };

  return {
    kind: event.name === 'Transfer' ? 'transfer' : 'approval',
    asset: resolvedAsset.address
      ? resolvedAsset
      : { ...resolvedAsset, address: tokenAddress },
    rawAmount: amount,
    source,
    destination,
  };
}

/**
 * Formats a token amount with at least two fractional digits. The sign is
 * normalized away for callers to convey through direction, while existing
 * precision beyond two digits is preserved.
 */
function displayAmount(rawAmount: bigint, decimals: number): string {
  const formatted = formatTokenAmountWithOptionalDecimals(
    rawAmount < 0n ? -rawAmount : rawAmount,
    decimals
  );
  const [integer, fraction] = formatted.split('.');

  // Case: whole token amounts still display the UI's two decimal places.
  if (fraction === undefined) {
    return `${integer}.00`;
  }

  // Case: a single fractional digit is padded without rounding away precision.
  if (fraction.length === 1) {
    return `${integer}.${fraction}0`;
  }

  return formatted;
}

/** Converts an approval amount into its finite or unlimited display model. */
function allowanceFor(event: ParsedTokenEvent): SimulationAllowance {
  // Case: max uint256 is the ERC-20 convention for an unlimited approval.
  if (event.rawAmount === maxUint256) {
    return { kind: 'unlimited' };
  }

  return {
    kind: 'amount',
    amount: displayAmount(event.rawAmount, event.asset.decimals),
  };
}

/** Preserves provider event order while converting token events into UI rows. */
function deriveTokenActivities(
  events: ParsedTokenEvent[]
): SimulationTokenActivity[] {
  return events.map((event): SimulationTokenActivity => {
    if (event.kind === 'transfer') {
      return {
        kind: 'transfer',
        asset: event.asset,
        amount: displayAmount(event.rawAmount, event.asset.decimals),
        from: event.source,
        to: event.destination,
      };
    }

    return {
      kind: 'approval',
      asset: event.asset,
      allowance: allowanceFor(event),
      owner: event.source,
      spender: event.destination,
    };
  });
}

/** Returns approvals granted by the simulated account for the summary card. */
function deriveTokenApprovals(
  events: ParsedTokenEvent[],
  simulatedAccountAddress: string
): SimulationTokenApproval[] {
  const accountKey = simulatedAccountAddress.toLowerCase();

  return events.flatMap((event): SimulationTokenApproval[] => {
    // Case: the summary excludes transfers and approvals owned by other accounts.
    if (
      event.kind !== 'approval' ||
      event.source.address.toLowerCase() !== accountKey
    ) {
      return [];
    }

    return [
      {
        asset: event.asset,
        allowance: allowanceFor(event),
        spender: event.destination,
      },
    ];
  });
}

/**
 * Nets all ERC-20 Transfer events against the simulated account. Self-transfers
 * become a zero net change, while first-seen token order is retained.
 */
function deriveErc20BalanceChanges(
  events: ParsedTokenEvent[],
  simulatedAccountAddress: string
): SimulationBalanceChange[] {
  const accountKey = simulatedAccountAddress.toLowerCase();
  const balances = new Map<string, { asset: SimulationAsset; delta: bigint }>();

  for (const event of events) {
    // Case: approvals change permissions, not token balances.
    if (event.kind !== 'transfer') {
      continue;
    }

    const sourceIsAccount = event.source.address.toLowerCase() === accountKey;
    const destinationIsAccount =
      event.destination.address.toLowerCase() === accountKey;

    // Case: unrelated transfers and self-transfers have no net account change.
    if (sourceIsAccount === destinationIsAccount) {
      continue;
    }

    const tokenKey = (event.asset.address ?? event.asset.symbol).toLowerCase();
    const current = balances.get(tokenKey) ?? {
      asset: event.asset,
      delta: 0n,
    };
    current.delta += sourceIsAccount ? -event.rawAmount : event.rawAmount;
    balances.set(tokenKey, current);
  }

  return [...balances.values()].flatMap(({ asset, delta }) =>
    delta === 0n
      ? []
      : [
          {
            asset,
            direction: delta > 0n ? 'increased' : 'decreased',
            amount: displayAmount(delta, asset.decimals),
          } satisfies SimulationBalanceChange,
        ]
  );
}

/**
 * Calculates the simulated account's net native-token change from backend
 * balance snapshots, excluding miner balances and malformed numeric values.
 */
function deriveNativeBalanceChange(
  response: TransactionSimulationResponse
): SimulationBalanceChange | undefined {
  const accountKey = response.resolution.simulatedAccountAddress.toLowerCase();
  const rawDiff = (response.result.balanceDiffs ?? [])
    .flatMap((diff) => diff.raw)
    .find((diff) => !diff.isMiner && diff.address.toLowerCase() === accountKey);

  // Case: the backend returned no non-miner balance snapshot for this account.
  if (rawDiff === undefined) {
    return undefined;
  }

  try {
    const delta = BigInt(rawDiff.dirty) - BigInt(rawDiff.original);
    // Case: an unchanged native balance should not create a presentation row.
    if (delta === 0n) {
      return undefined;
    }

    return {
      asset: response.resolution.nativeAsset,
      direction: delta > 0n ? 'increased' : 'decreased',
      amount: displayAmount(delta, response.resolution.nativeAsset.decimals),
    };
  } catch {
    // Case: malformed backend balance values cannot produce a reliable row.
    return undefined;
  }
}

/**
 * Builds the sheet's consumer-specific presentation. Unsupported token logs and
 * irrelevant or zero-net balance changes are omitted; native balance changes
 * precede first-seen ERC-20 changes; account-owned approvals and token activity
 * retain provider event order.
 */
export function deriveSimulationPresentation(
  response: TransactionSimulationResponse
): DerivedSimulationPresentation {
  const events = (response.result.eventLogs ?? []).flatMap((event) => {
    const parsed = parseTokenEvent(event, response.resolution);
    return parsed === undefined ? [] : [parsed];
  });
  const erc20BalanceChanges = deriveErc20BalanceChanges(
    events,
    response.resolution.simulatedAccountAddress
  );
  const nativeBalanceChange = deriveNativeBalanceChange(response);
  const tokenActivities = deriveTokenActivities(events);

  // Native transfers have no ERC-20 Transfer event. Repeating the account's
  // net change avoids presenting gas or internal calls as user-facing transfers.
  if (nativeBalanceChange !== undefined) {
    const accountAddress = response.resolution.simulatedAccountAddress;
    tokenActivities.push({
      kind: 'native-balance-change',
      ...nativeBalanceChange,
      account: response.resolution.partiesByAddress[
        accountAddress.toLowerCase()
      ] ?? { address: accountAddress },
    });
  }

  return {
    balanceChanges:
      nativeBalanceChange === undefined
        ? erc20BalanceChanges
        : [nativeBalanceChange, ...erc20BalanceChanges],
    tokenApprovals: deriveTokenApprovals(
      events,
      response.resolution.simulatedAccountAddress
    ),
    tokenActivities,
  };
}

/**
 * Validates an unknown backend response, then maps its terminal simulation
 * status and presentation data into the discriminated state consumed by the
 * transaction sheet.
 */
export function toTransactionSimulationView(
  response: unknown
): CompletedTransactionSimulationState {
  const parsedResponse = transactionSimulationResponseSchema.parse(response);
  const { result } = parsedResponse;

  switch (result.status) {
    case SimulationStatus.TRANSACTION_SUCCEEDED: {
      const presentation = deriveSimulationPresentation(parsedResponse);

      return {
        status: 'success',
        metadata: {
          gas: {
            ...(result.gasUsed == null
              ? {}
              : {
                  used: result.gasUsed,
                }),
          },
        },
        balanceChanges: presentation.balanceChanges,
        tokenApprovals: presentation.tokenApprovals,
        tokenActivities: presentation.tokenActivities,
      };
    }
    case SimulationStatus.TRANSACTION_REVERTED:
      return {
        status: 'reverted',
        reason: result.revertString ?? 'Transaction reverted',
      };
    case SimulationStatus.SIMULATION_FAILED:
      return {
        status: 'provider-failure',
        message: result.revertString ?? 'Please report this to the Den team.',
        retryable: true,
      };
    default: {
      return {
        status: 'provider-failure',
        message: 'The simulation returned an unsupported status.',
        retryable: false,
      };
    }
  }
}
